import React, { useMemo } from "react";
import FilterContainer, { CoreFiltersBaseProps } from "../../../../../components/FilterContainer";
import { Collapse, Form, Input, Select, Tag } from "antd";
import DateRangePicker from "../../../../../../../components/forms/controls/DateRangePicker";
import { useGetAllUsersQuery } from "../../../../../../../store/services/api/user-api";
import { useGetAllAssetTypesQuery, useGetAllSubAssetTypesQuery } from "../../../../../../../store/services/api/asset-type-api";
import { DateTime } from "luxon";


const { Panel } = Collapse;

export default function JobPlansListScreenFilters({ filter, onSubmit }: CoreFiltersBaseProps) {
  const { data: allUsers } = useGetAllUsersQuery();
  const { data: assetTypes } = useGetAllAssetTypesQuery(null);
  const { data: subAssetTypes } = useGetAllSubAssetTypesQuery(null);

  const selectedAssetTypeIds: string[] = filter.getFilter("assetTypeId") || [];
  const selectedSubAssetTypeIds: string[] = filter.getFilter("subAssetTypeId") || [];


  const handleOnAssetTypeChange = (assetTypeIds: string[]) => {
    filter.setFilter("assetTypeId", assetTypeIds);

    // prune sub assets
    const currentSubAssets: string[] = filter.getFilter("subAssetTypeId") || [];
    const validSubAssets = (subAssetTypes || [])
      .filter((sa) => assetTypeIds.includes(sa.assetTypeId)) // assumes subAsset has `assetTypeId` reference
      .map((sa) => sa.id);

    const prunedSubAssets = currentSubAssets.filter((saId) =>
      validSubAssets.includes(saId)
    );
    filter.setFilter("subAssetTypeId", prunedSubAssets);
  };
  

    const filteredSubAssets = !selectedAssetTypeIds.length
    ? subAssetTypes || []
    : (subAssetTypes || []).filter((sa) =>
        selectedAssetTypeIds.includes(sa.assetTypeId)
      );

  // Create label with AssetType
  const subAssetLabelMap: Map<string, string> = new Map(
    filteredSubAssets.map((sa) => {
      const assetName =
        assetTypes?.find((a) => a.id === sa.assetTypeId)?.name ?? "";
      return [sa.id, `[${assetName}] ${sa.name}`];
    })
  );

  const sortedSubAssets = filteredSubAssets
    .map((sa) => ({
      ...sa,
      label: subAssetLabelMap.get(sa.id) || sa.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const subAssetTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const subAssetLabel = subAssetLabelMap.get(value) || label;
        
        return (
          <Tag
            closable={closable}
            onClose={onClose}
            style={{ 
              margin: '2px', 
              padding: '2px 6px',
              maxWidth: '100%',
              whiteSpace: 'normal',
              lineHeight: '1.4em',
              height: 'auto'
            }}
          >
            {subAssetLabel}
          </Tag>
        );
      };

  return (
    <FilterContainer onResetFilters={filter.resetFilters} onSubmit={onSubmit} isSubmitting={undefined}>
      <Collapse defaultActiveKey={['general', 'assetInfo', 'createdInfo', 'status']} ghost>
        
        {/* General Info */}
        <Panel header="General Info" key="general">
          <Form layout="vertical">
            <Form.Item label="Reference">
              <Input
                allowClear
                value={filter.getFilter('reference')}
                onChange={e => filter.setFilter('reference', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Description">
              <Input
                allowClear
                value={filter.getFilter('title')}
                onChange={e => filter.setFilter('title', e.target.value)}
              />
            </Form.Item>
          </Form>
        </Panel>

        {/* Asset Info */}
        <Panel header="Asset Info" key="assetInfo">
          <Form layout="vertical">
            <Form.Item label="Asset Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedAssetTypeIds}
                onChange={handleOnAssetTypeChange}
                options={assetTypes?.map((item) => ({ label: item.name, value: item.id }))}
              />
            </Form.Item>



            <Form.Item label="Sub Asset Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).searchText || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedSubAssetTypeIds}
                onChange={(val) => filter.setFilter('subAssetTypeId', val)}
                tagRender={subAssetTagRender}
                dropdownRender={menu => (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {menu}
                  </div>
                )}
                optionLabelProp="label"
                options={sortedSubAssets.map(item => ({
                  label: (
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                      {item.label}
                    </div>
                  ),
                  value: item.id,
                  searchText: item.label,
                }))}
              />
            </Form.Item>
          </Form>
        </Panel>

        {/* Created Info */}
        <Panel header="Created/Approved Info" key="createdInfo">
          <Form layout="vertical">
            <Form.Item label="Created By">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).label  || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('createdByUserId')}
                onChange={val => filter.setFilter('createdByUserId', val)}
                options={allUsers?.map(u => ({ label: u.name, value: u.id }))}
              />
            </Form.Item>
            <Form.Item label="Created On">
              <DateRangePicker
                onStartDateChange={val => filter.setFilter('createdOnStartDate', val?.toISO())}
                onEndDateChange={val => filter.setFilter('createdOnEndDate', val?.toISO())}
                startDateValue={filter.getFilter('createdOnStartDate') ? DateTime.fromISO(filter.getFilter('createdOnStartDate')) : undefined}
                endDateValue={filter.getFilter('createdOnEndDate') ? DateTime.fromISO(filter.getFilter('createdOnEndDate')) : undefined}
              />
            </Form.Item>
            <Form.Item label="Approved By">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).label  || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('approvedByUserId')}
                onChange={val => filter.setFilter('approvedByUserId', val)}
                options={allUsers?.map(u => ({ label: u.name, value: u.id }))}
              />
            </Form.Item>
          </Form>
        </Panel>

        {/* Status */}
        <Panel header="Status" key="status">
          <Form layout="vertical">
            <Form.Item label="Status">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).children  || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('status')}
                onChange={val => filter.setFilter('status', val)}
              >
                <Select.Option value="0">Awaiting Approval</Select.Option>
                <Select.Option value="1">Approved</Select.Option>
                <Select.Option value="2">Rejected</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Panel>

      </Collapse>
    </FilterContainer>
  );
}
